/**
 * useAuthGuard - Hook for protecting routes that require authentication
 * Eliminates ~200 lines of duplicated auth checking code across 8+ pages
 *
 * Usage:
 * ```tsx
 * const { isLoading } = useAuthGuard({ redirectTo: '/login' });
 * if (isLoading) return <Loading />;
 * // User is authenticated, render protected content
 * ```
 */

"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { authService } from "@/lib/auth";

interface UseAuthGuardOptions {
  /**
   * Path to redirect to if user is not authenticated
   * @default "/login"
   */
  redirectTo?: string;

  /**
   * If true, redirects authenticated users away (for login/register pages)
   * @default false
   */
  requireGuest?: boolean;

  /**
   * Path to redirect authenticated users to when requireGuest is true
   * @default "/"
   */
  guestRedirectTo?: string;
}

interface UseAuthGuardReturn {
  /**
   * True while checking authentication status
   */
  isLoading: boolean;

  /**
   * True if user is authenticated
   */
  isAuthenticated: boolean;
}

export function useAuthGuard(
  options: UseAuthGuardOptions = {}
): UseAuthGuardReturn {
  const {
    redirectTo = "/login",
    requireGuest = false,
    guestRedirectTo = "/",
  } = options;

  const router = useRouter();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsAuthenticated(authenticated);

      if (requireGuest) {
        // For login/register pages - redirect if already authenticated
        if (authenticated) {
          router.push(guestRedirectTo);
        } else {
          setIsLoading(false);
        }
      } else {
        // For protected pages - redirect if not authenticated
        if (!authenticated) {
          router.push(redirectTo);
        } else {
          setIsLoading(false);
        }
      }
    };

    // Use setTimeout to avoid blocking render
    const timer = setTimeout(checkAuth, 0);
    return () => clearTimeout(timer);
  }, [router, redirectTo, requireGuest, guestRedirectTo]);

  return { isLoading, isAuthenticated };
}
